import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Edit, Trash2, ArrowLeft, Eye, Shield } from 'lucide-react';
import { useAlert } from '../../context/AlertContext';
import { useSite } from '../../context/SiteContext';
import { usePermissions } from '../../context/PermissionContext';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ENTITY_TYPES = ['incident', 'document', 'hazard', 'risk_assessment', 'permit'];
const VISIBILITY_SCOPES = [
  { value: 'all_sites', label: 'All Sites' },
  { value: 'same_parent_group', label: 'Same Parent Group' },
  { value: 'same_country', label: 'Same Country' },
  { value: 'specific_sites', label: 'Specific Sites' }
];
const OPERATORS = ['equals', 'in', 'gte', 'any'];

// REL-19: the API stores conditions as {"value": x} or {"values": [...]}.
// Show them the way the form accepts them (plain text or a JSON array) so an
// edit round-trips instead of re-sending the stored object as a string.
const conditionToText = (cv) => {
  if (cv === null || cv === undefined) return '';
  if (typeof cv !== 'object') return String(cv);
  if (Array.isArray(cv)) return JSON.stringify(cv);
  if (Array.isArray(cv.values)) return JSON.stringify(cv.values);
  if (cv.value !== undefined && cv.value !== null) return String(cv.value);
  return JSON.stringify(cv);
};

const CrossSiteRules = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError, confirm } = useAlert();
  const { availableSites } = useSite();
  const { hasRole } = usePermissions();

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    entity_type: 'incident',
    condition_field: '',
    condition_operator: 'equals',
    condition_value: '',
    visibility_scope: 'all_sites',
    target_sites: [],
    is_active: true
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/org/visibility-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRules(response.data || []);
    } catch (err) {
      console.error('Failed to fetch rules:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (rule = null) => {
    if (rule) {
      setEditingRule(rule);
      setFormData({
        entity_type: rule.entity_type,
        condition_field: rule.condition_field || '',
        condition_operator: rule.condition_operator || 'equals',
        condition_value: conditionToText(rule.condition_value),
        visibility_scope: rule.visibility_scope,
        target_sites: rule.target_sites || [],
        is_active: rule.is_active
      });
    } else {
      setEditingRule(null);
      setFormData({
        entity_type: 'incident',
        condition_field: '',
        condition_operator: 'equals',
        condition_value: '',
        visibility_scope: 'all_sites',
        target_sites: [],
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');

      // Parse condition_value to JSON if it looks like an array
      let conditionValue = (formData.condition_value || '').trim();
      if (conditionValue.startsWith('[')) {
        try { conditionValue = JSON.parse(conditionValue); } catch (e) { /* keep as string */ }
      }

      const payload = {
        ...formData,
        condition_value: conditionValue,
        target_sites: formData.visibility_scope === 'specific_sites' ? formData.target_sites : null
      };

      if (editingRule) {
        await axios.put(`${API}/org/visibility-rules/${editingRule.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSuccess('Rule updated successfully.');
      } else {
        await axios.post(`${API}/org/visibility-rules`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        showSuccess('Rule created successfully.');
      }

      setShowModal(false);
      fetchRules();
    } catch (err) {
      const detail = err.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Failed to save rule.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (ruleId) => {
    const confirmed = await confirm('Delete this visibility rule?', 'This action cannot be undone.');
    if (!confirmed) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/org/visibility-rules/${ruleId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showSuccess('Rule deleted.');
      fetchRules();
    } catch (err) {
      showError('Failed to delete rule.');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      const token = localStorage.getItem('token');
      // Send only the flag: the server re-validates the stored rule.
      await axios.put(`${API}/org/visibility-rules/${rule.id}`, {
        is_active: !rule.is_active
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchRules();
    } catch (err) {
      showError(err.response?.data?.detail || 'Failed to toggle rule status.');
    }
  };

  const handleTargetSiteToggle = (siteId) => {
    setFormData(prev => ({
      ...prev,
      target_sites: prev.target_sites.includes(siteId)
        ? prev.target_sites.filter(id => id !== siteId)
        : [...prev.target_sites, siteId]
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-[calc(100vh-64px)]">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <Button variant="outline" onClick={() => navigate('/admin')} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Admin
          </Button>

          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Cross-Site Visibility Rules</h1>
              <p className="text-gray-600 mt-1">Configure which records are visible across sites</p>
            </div>
            <Button onClick={() => handleOpenModal()} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Add Rule
            </Button>
          </div>
        </div>

        {/* Rules Table */}
        <Card>
          <CardContent className="p-0">
            {rules.length === 0 ? (
              <div className="py-12 text-center">
                <Shield className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No visibility rules configured yet.</p>
                <Button onClick={() => handleOpenModal()} className="mt-4">
                  Create First Rule
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Entity Type</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Condition</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Visibility Scope</th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Status</th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rules.map((rule) => (
                      <tr key={rule.id} className="border-b hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm font-medium capitalize">
                            {rule.entity_type}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {rule.condition_field ? (
                            <span>
                              {rule.condition_field} {rule.condition_operator}{' '}
                              <code className="bg-gray-100 px-1 rounded">
                                {conditionToText(rule.condition_value)}
                              </code>
                            </span>
                          ) : (
                            <span className="text-gray-400 italic">All records</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-sm font-medium">
                            {VISIBILITY_SCOPES.find(s => s.value === rule.visibility_scope)?.label || rule.visibility_scope}
                          </span>
                          {rule.target_sites?.length > 0 && (
                            <span className="text-xs text-gray-500 ml-2">
                              ({rule.target_sites.length} sites)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => handleToggleActive(rule)}
                            className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                              rule.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            {rule.is_active ? 'Active' : 'Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleOpenModal(rule)}
                            >
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(rule.id)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">
                  {editingRule ? 'Edit Visibility Rule' : 'Add Visibility Rule'}
                </h2>

                <div className="space-y-4">
                  {/* Entity Type */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Entity Type</label>
                    <select
                      value={formData.entity_type}
                      onChange={(e) => setFormData({ ...formData, entity_type: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {ENTITY_TYPES.map(type => (
                        <option key={type} value={type}>{type.replace('_', ' ')}</option>
                      ))}
                    </select>
                  </div>

                  {/* Condition Field */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      Condition Field <span className="text-xs text-gray-400">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.condition_field}
                      onChange={(e) => setFormData({ ...formData, condition_field: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      placeholder="e.g., severity, incident_type"
                    />
                  </div>

                  {/* Operator */}
                  {formData.condition_field && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Operator</label>
                      <select
                        value={formData.condition_operator}
                        onChange={(e) => setFormData({ ...formData, condition_operator: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                      >
                        {OPERATORS.map(op => (
                          <option key={op} value={op}>{op}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Condition Value */}
                  {formData.condition_field && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Condition Value</label>
                      <input
                        type="text"
                        value={formData.condition_value}
                        onChange={(e) => setFormData({ ...formData, condition_value: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                        placeholder='e.g., "fatality" or ["major","catastrophic"]'
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        Use JSON array format for multiple values: ["value1","value2"]
                      </p>
                    </div>
                  )}

                  {/* Visibility Scope */}
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Visibility Scope</label>
                    <select
                      value={formData.visibility_scope}
                      onChange={(e) => setFormData({ ...formData, visibility_scope: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
                    >
                      {VISIBILITY_SCOPES.map(scope => (
                        <option key={scope.value} value={scope.value}>{scope.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Target Sites (when specific_sites) */}
                  {formData.visibility_scope === 'specific_sites' && (
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-1">Select Sites</label>
                      <div className="max-h-40 overflow-y-auto border border-gray-300 rounded-lg p-2 space-y-1">
                        {availableSites.map(site => (
                          <label key={site.site_id} className="flex items-center gap-2 px-2 py-1 hover:bg-gray-50 rounded cursor-pointer">
                            <input
                              type="checkbox"
                              checked={formData.target_sites.includes(site.site_id)}
                              onChange={() => handleTargetSiteToggle(site.site_id)}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm">{site.site_name}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Active Toggle */}
                  <div className="flex items-center gap-3">
                    <label className="text-sm font-semibold text-gray-700">Active</label>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                      className={`relative w-11 h-6 rounded-full transition-colors ${
                        formData.is_active ? 'bg-blue-600' : 'bg-gray-300'
                      }`}
                    >
                      <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${
                        formData.is_active ? 'translate-x-5' : ''
                      }`} />
                    </button>
                  </div>
                </div>

                {/* Modal Actions */}
                <div className="flex gap-3 mt-6 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => setShowModal(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                  >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    {saving ? 'Saving...' : editingRule ? 'Update Rule' : 'Create Rule'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CrossSiteRules;
